/**
 * Board 1 · The prepared protocol: what Ready means, where its live tickets stand, the protocol's switch, its
 * promoted and not promoted scenarios, a summary of its writes, and the way to History. Board 6's banner leads it
 * while the promotion is on old wording.
 */

import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import type { PromotionJob } from '@/modules/promotion/types';
import type { LivePoolView } from '../types';
import { formatWhen, isJobActive, isPromoted, promotedSummaryOf } from './scenarios';
import {
	replyProgressLineOf,
	replyProgressOf,
	setAsideLineOf,
	writeSummariesOf,
	type OverviewRow,
	type WriteSummary
} from './view-rules';

/** While the replies are being written: how many are done, and where the rest stand. */
export const ReplyProgressCard = ({ job }: { job: Pick<PromotionJob, 'types'> }) => {
	const progress = replyProgressOf(job);
	const percent = progress.total ? Math.round((progress.settled / progress.total) * 100) : 0;
	return (
		<div className='flex flex-col gap-1.5 rounded-[10px] border border-blue-border bg-blue-bg px-3 py-2.5' role='status'>
			<span className='flex items-center gap-1.5 text-xs font-medium text-blue-foreground'>
				<Loader2 className='size-3.5 animate-spin' aria-hidden />
				Writing replies · {progress.settled} of {progress.total} done
			</span>
			<div className='h-1 overflow-hidden rounded-full bg-gray-100' aria-hidden>
				<div className='h-full bg-blue-600 transition-[width]' style={{ width: `${percent}%` }} />
			</div>
			<span className='text-[11px] text-blue-foreground'>{replyProgressLineOf(progress)}</span>
		</div>
	);
};

/** The last attempt to write the replies started nothing: why, and a way to try again. */
export const ReplyWritingBanner = ({
	error,
	canEdit,
	preparing,
	onWriteReplies
}: {
	error: string;
	canEdit: boolean;
	preparing: boolean;
	onWriteReplies: () => void;
}) => (
	<div className='flex flex-col gap-2 rounded-[10px] border border-yellow-border bg-yellow-bg px-3 py-2.5' role='alert'>
		<span className='text-[13px] font-medium text-yellow-foreground'>The replies weren't written.</span>
		<span className='text-xs text-yellow-foreground'>{error}</span>
		{canEdit && (
			<Button size='sm' variant='outline' className='self-start' disabled={preparing} onClick={onWriteReplies}>
				{preparing ? 'Starting…' : 'Try again'}
			</Button>
		)}
	</div>
);

/** Board 6 · The protocol was compiled again after it was promoted. */
export const OutdatedBanner = ({
	outdated,
	canEdit,
	busy,
	onPromoteAgain
}: {
	outdated: NonNullable<LivePoolView['outdated']>;
	canEdit: boolean;
	busy: boolean;
	onPromoteAgain: () => void;
}) => {
	const lastAlert = outdated.alertsSent.length ? Math.max(...outdated.alertsSent) : null;
	return (
		<div className='flex flex-col gap-2 rounded-[10px] border border-yellow-border bg-yellow-bg px-3 py-2.5' role='status'>
			<span className='text-[13px] font-medium text-yellow-foreground'>
				The protocol changed. Promoted replies still use the old wording.
			</span>
			<span className='text-xs text-yellow-foreground'>
				Recompiled {formatWhen(outdated.since)}. Since then, {outdated.replies} replies went out on the old wording.
				{lastAlert !== null && ` Alert sent to the promotion channel at ${lastAlert}.`}
			</span>
			<Button size='sm' className='w-fit' disabled={!canEdit || busy} onClick={onPromoteAgain}>
				Review and promote again
			</Button>
		</div>
	);
};

/**
 * One write of the protocol. The reject alert gets its own line, clamped to two: a scenario without a short name
 * is labelled by its whole description, which must wrap inside the card, never widen it.
 */
export const WriteSummaryCard = ({ write }: { write: WriteSummary }) => {
	const alert = write.worst ? `Reject rate ${write.worst.rate} in ${write.worst.scenario}` : null;
	return (
		<div className='flex min-w-0 flex-col gap-0.5 rounded-[10px] border border-gray-500 px-3 py-2.5'>
			<span className='font-mono text-xs text-text-heading'>{write.tool}</span>
			<span className='text-[11px] text-gray-800'>
				Used by {write.scenarios} {write.scenarios === 1 ? 'scenario' : 'scenarios'} · auto in {write.auto} ·{' '}
				{write.rejected} rejects in 30 days
			</span>
			{alert && (
				<span title={alert} className='mt-1 line-clamp-2 rounded-md bg-red-bg px-2 py-1 text-[11px] leading-4 text-red-foreground'>
					{alert}
				</span>
			)}
		</div>
	);
};

interface OverviewProps {
	view: LivePoolView;
	/** The protocol's latest reply generation; its progress shows while it runs. */
	job: PromotionJob | null | undefined;
	rows: OverviewRow[];
	/** The active promotion is on. */
	on: boolean;
	autoWrites: number;
	canEdit: boolean;
	busy: boolean;
	sorting: boolean;
	/** Prepare is starting: the sort, then the replies. */
	preparing: boolean;
	readyRule: string;
	renderCard: (row: OverviewRow) => React.ReactNode;
	onProtocolSwitch: (on: boolean) => void;
	onSort: () => void;
	onPromoteAgain: () => void;
	onHistory: () => void;
	/** Sorts the live tickets again, then writes the replies still missing. */
	onWriteReplies: () => void;
}

export const Overview = ({
	view,
	job,
	rows,
	on,
	autoWrites,
	canEdit,
	busy,
	sorting,
	preparing,
	readyRule,
	renderCard,
	onProtocolSwitch,
	onSort,
	onPromoteAgain,
	onHistory,
	onWriteReplies
}: OverviewProps) => {
	const promoted = rows.filter((row) => isPromoted(row.state));
	const notPromoted = rows.filter((row) => !isPromoted(row.state));
	const writes = writeSummariesOf(rows);
	const setAsideLine = setAsideLineOf(view.pool.setAside, view.setAsideReasons);
	return (
		<div className='flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-5 py-4'>
			{view.outdated && (
				<OutdatedBanner outdated={view.outdated} canEdit={canEdit} busy={busy} onPromoteAgain={onPromoteAgain} />
			)}
			{job && isJobActive(job) && <ReplyProgressCard job={job} />}
			{view.replyWriting && (
				<ReplyWritingBanner
					error={view.replyWriting.error}
					canEdit={canEdit}
					preparing={preparing || sorting}
					onWriteReplies={onWriteReplies}
				/>
			)}
			<div className='flex flex-col gap-1'>
				<p className='flex justify-between text-[13px] font-medium text-text-heading'>
					Scenarios <span className='text-[11px] font-normal text-gray-800'>{rows.length} total</span>
				</p>
				<p className='text-[11px] leading-4 text-gray-800'>
					<b className='font-medium text-text-heading'>Auto-send</b> = the reply goes out by itself.{' '}
					<b className='font-medium text-text-heading'>Draft</b> = Kim drafts it and a person sends it.{' '}
					<b className='font-medium text-text-heading'>Ready</b> = {readyRule}.
				</p>
			</div>
			<div className='flex flex-col gap-1 rounded-lg bg-blue-bg px-3 py-2 text-[11px] leading-4 text-blue-foreground'>
				<span>
					{rows.length} scenarios from {view.pool.recorded} live tickets Kim answered on this protocol in the last{' '}
					{view.windowDays} days, and {view.pool.gaps} that fit no scenario. Nothing changes for customers until you
					switch one on.
				</span>
				{setAsideLine && <span>{setAsideLine}</span>}
				{view.pool.open > 0 && (
					<span>
						{view.pool.open} new live tickets not sorted yet.{' '}
						<button
							type='button'
							className='cursor-pointer font-medium underline disabled:cursor-default disabled:no-underline'
							onClick={onSort}
							disabled={!canEdit || sorting}
						>
							{sorting ? 'Sorting…' : 'Sort them'}
						</button>
					</span>
				)}
			</div>
			<div className='flex items-center justify-between gap-3 rounded-[10px] border border-gray-500 px-3 py-2.5'>
				<div className='flex flex-col gap-0.5'>
					<span className='text-[13px] font-medium text-text-heading'>Promoted on this protocol</span>
					<span className='text-[11px] text-gray-800'>
						{promotedSummaryOf(
							rows.map((row) => row.state),
							autoWrites
						)}
					</span>
				</div>
				<Switch
					checked={on}
					disabled={!canEdit || busy}
					onCheckedChange={onProtocolSwitch}
					aria-label='Promoted on this protocol'
				/>
			</div>
			<p className='flex justify-between text-[11px] font-medium tracking-wide text-gray-800 uppercase'>
				Promoted <span>{promoted.length}</span>
			</p>
			<div className='flex flex-col gap-2'>
				{promoted.length ? promoted.map(renderCard) : <p className='text-xs text-gray-800'>None yet.</p>}
			</div>
			<p className='flex justify-between text-[11px] font-medium tracking-wide text-gray-800 uppercase'>
				Not promoted <span>{notPromoted.length}</span>
			</p>
			<div className='flex flex-col gap-2'>{notPromoted.map(renderCard)}</div>
			{writes.length > 0 && (
				<div className='flex flex-col gap-2 border-t border-gray-300 pt-3'>
					<p className='text-[13px] font-medium text-text-heading'>Write actions</p>
					<p className='text-[11px] leading-4 text-gray-800'>
						Each scenario has its own auto-approve switch. A No in one scenario never counts against another.
					</p>
					{writes.map((write) => (
						<WriteSummaryCard key={write.tool} write={write} />
					))}
				</div>
			)}
			<button type='button' onClick={onHistory} className='w-fit cursor-pointer text-xs font-medium text-blue-foreground underline'>
				History
			</button>
		</div>
	);
};
